import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ClientAccount, ClientAccountSummaryArgs } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import { buildQueryKey } from 'utils/buildQueryKey';
import clientUtil from 'utils/clientUtil';

type TVariables = ClientAccountSummaryArgs;
type TData = Pick<ClientAccount, 'summary'>;

const MOCK: TVariables = {
  comments: undefined,
  createdAfter: undefined,
  createdBefore: undefined,
  editorId: undefined,
  fromDate: undefined,
  proposedClientDeadlineAfter: undefined,
  proposedClientDeadlineBefore: undefined,
  quality: undefined,
  query: undefined,
  showDuplicates: undefined,
  sourceLanguageCode: undefined,
  status: undefined,
  targetLanguageCode: undefined,
  toDate: undefined
};

export const useSummary = (
  variables: TVariables = MOCK,
  options: Omit<UseQueryOptions<unknown, TGenericError, TData>, 'queryKey'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query useSummary(
      $query: String
      $comments: CommentsFilterEnum
      $status: [TranslationRequestStatusFilterEnum]
      $quality: QualityEnum
      $showDuplicates: Boolean
      $targetLanguageCode: String
      $sourceLanguageCode: String
      $editorId: ID
      $createdAfter: ISO8601DateTime
      $createdBefore: ISO8601DateTime
      $proposedClientDeadlineBefore: ISO8601DateTime
      $proposedClientDeadlineAfter: ISO8601DateTime
    ) {
      clientAccount {
        summary(
          query: $query
          comments: $comments
          status: $status
          quality: $quality
          showDuplicates: $showDuplicates
          targetLanguageCode: $targetLanguageCode
          sourceLanguageCode: $sourceLanguageCode
          editorId: $editorId
          createdAfter: $createdAfter
          createdBefore: $createdBefore
          proposedClientDeadlineBefore: $proposedClientDeadlineBefore
          proposedClientDeadlineAfter: $proposedClientDeadlineAfter
        ) {
          ordered {
            sentences
            words
          }
          translated {
            sentences
            words
          }
          savings {
            percentage
            words
          }
        }
      }
    }
  `;

  return useQuery<unknown, TGenericError, TData>({
    ...options,
    queryKey: ['summary', ...buildQueryKey({ ...MOCK, ...variables })],
    queryFn: async () =>
      client.request({
        data: {
          gql: graphql,
          variables
        }
      })
  });
};
